import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import { PROFILE, PHOTOS, SKILLS } from "../../data/content";
import { downloadResume } from "../../lib/downloadResume";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function ProHome() {
  const openResume = async () => {
    if (!PROFILE.resumeUrl) {
      toast("Resume not available yet");
      return;
    }
    toast("Downloading résumé", { description: PROFILE.resumeFilename });
    await downloadResume();
  };

  return (
    <div className="pt-24 pb-16" data-testid="pro-home">
      <section className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <motion.div
            className="md:col-span-7"
            variants={fadeUp}
            initial="hidden"
            animate="show"
          >
            <p className="font-plexMono text-xs uppercase tracking-[0.2em] text-terracotta mb-6">
              <MapPin className="inline -mt-0.5 mr-1" size={12} />
              Currently in Bangalore
            </p>
            <h1 className="font-serif font-light text-5xl md:text-7xl leading-[1.05] tracking-tight text-neutral-900">
              {PROFILE.fullName || PROFILE.name}.
            </h1>
            <p className="font-plex text-lg md:text-xl text-neutral-600 mt-6 max-w-xl leading-relaxed">
              {PROFILE.role}. I implement, customise and upgrade{" "}
              <span className="text-neutral-900 font-medium">Microsoft Dynamics 365 Business Central</span>{" "}
              for teams across manufacturing, finance, sales and warehouse operations.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button
                onClick={openResume}
                className="inline-flex items-center gap-2 bg-neutral-900 text-white px-5 py-3 rounded-full text-sm font-plex font-medium hover:bg-terracotta transition-colors duration-200"
                data-testid="pro-home-download-resume"
              >
                <Download size={16} /> Download résumé
              </button>
              <Link
                to="/experience"
                className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-900 px-5 py-3 rounded-full text-sm font-plex font-medium hover:border-neutral-900 transition-colors duration-200"
                data-testid="pro-home-see-experience"
              >
                See experience <ArrowRight size={16} />
              </Link>
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-900 px-5 py-3 rounded-full text-sm font-plex font-medium hover:border-neutral-900 transition-colors duration-200"
                data-testid="pro-home-see-projects"
              >
                See projects <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>

          <motion.div
            className="md:col-span-5"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          >
            <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden bg-neutral-100">
              <img
                src={PHOTOS.portrait}
                alt="Arya Kadulkar"
                className="w-full h-full object-cover object-[center_18%]"
                loading="eager"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* METRICS */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 mt-24">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8 border-t border-neutral-200 pt-10">
          {[
            { n: "3+", l: "Years" },
            { n: "20+", l: "Projects" },
            { n: "2", l: "Cities" },
          ].map((m) => (
            <div key={m.l}>
              <p className="font-serif text-4xl md:text-5xl text-neutral-900">{m.n}</p>
              <p className="font-plexMono text-xs uppercase tracking-widest text-neutral-500 mt-2">
                {m.l}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* SKILL STRIP */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 mt-20">
        <p className="font-plexMono text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">
          Core toolkit
        </p>
        <div className="flex flex-wrap gap-2">
          {SKILLS.map((s) => (
            <span
              key={s}
              className="font-plexMono text-xs px-3 py-1.5 border border-neutral-200 rounded-full text-neutral-700"
            >
              {s}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
